
import React from 'react';
import { Bell, ChevronDown, Copy, Download, Link } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { TopbarLanguageDropdown } from '@/components/common/TopbarLanguageDropdown';
import { useToast } from '@/components/ui/use-toast';
import { useAffiliateInfo } from '@/hooks/useAffiliate';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export const AffiliateTopbar = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: affiliateInfo } = useAffiliateInfo();
  
  const copyReferralLink = () => {
    if (!affiliateInfo?.referral_code) return;
    
    const baseUrl = window.location.origin;
    const referralUrl = `${baseUrl}/?ref=${affiliateInfo.referral_code}`;
    
    navigator.clipboard.writeText(referralUrl);
    toast({
      title: "Copied to clipboard",
      description: "Referral link has been copied to clipboard."
    });
  };
  
  return (
    <header className="h-16 flex items-center justify-between px-6 border-b border-border bg-background/95">
      <div className="flex items-center">
        <h1 className="text-xl font-bold">Affiliate Program</h1>
      </div>
      
      <div className="flex items-center space-x-4">
        {affiliateInfo?.status === 'active' && (
          <div className="flex items-center mr-4">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-background/80 border rounded-md">
                    <code className="text-xs font-mono">{affiliateInfo.referral_code}</code>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={copyReferralLink}>
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Copy your referral code</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <Button variant="ghost" size="sm" className="hidden lg:flex">
              <Link className="mr-2 h-4 w-4" />
              Create Custom Link
            </Button>
          </div>
        )}
        
        <TopbarLanguageDropdown />
        
        {/* Help dropdown was removed from here */}
        
        <Button variant="ghost" size="icon">
          <Bell size={20} />
        </Button>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-9 w-9 rounded-full">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-botscheme-royal-purple/20 text-botscheme-royal-purple">
                  {user?.user_metadata?.name?.split(' ')
                    .map(n => n[0])
                    .join('')
                    .toUpperCase() || 'TB'}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" forceMount>
            <DropdownMenuLabel>{user?.user_metadata?.name || 'User'}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/')}>
              Back to Main Dashboard
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              Account Settings
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
